/* Ingredients — filter the profiles, open one in the rail.
 *
 * The same grammar as assets/elsewhere-router.js, one aisle over: cards indexed from the DOM once,
 * chips built from the cards with live counts, a control with nothing to match hiding itself,
 * show-and-hide rather than rebuild, and the rail as the place a selection opens without losing
 * your place in the grid. Two facets here rather than one — the kind and the first letter —
 * because 221 cards need a second axis and a letter is how anyone already thinks about a list of
 * plants.
 *
 * THE ORDER IS ALPHABETICAL, done here. Liquid hands the metaobjects over in store order, which is
 * roughly the order they were written — batch 1's kitchen spices first, the fruits last — and a
 * list of 221 names in no order a reader can predict is a list they cannot scan. The cards are
 * re-appended by title once, before the first render, so a crawler reads the same page it would
 * without JavaScript and a person reads one they can use.
 *
 * THE FIFTY CHECK. The section prints the count the store expects on the grid; if the DOM holds
 * fewer, Liquid's 50-per-type ceiling has come back (sections/ingredients-router.liquid explains
 * the paginate wrapper that lifts it) and the console says so. A page silently showing a fifth of
 * the shop is the exact failure the old ingredient guide had.
 *
 * Every card is a real <a href> underneath, so none of this is load-bearing for a crawler or for
 * someone with JavaScript off.
 */
(() => {
  const app = document.getElementById('ckg-router');
  if (!app || app.dataset.surface !== 'ingredients') return;

  const $ = (id) => document.getElementById(id);
  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
  const track = (name, payload) => { try { window.Shopify?.analytics?.publish?.(name, { surface: 'ingredients', ...payload }); } catch (_) { /* never */ } };

  /* ---------- the shop's own words for a kind ---------- */
  const KINDS = [
    ['herb', 'Herbs'],
    ['spice', 'Spices'],
    ['tea-base', 'Teas'],
    ['fruit', 'Fruit'],
    ['flower', 'Flowers'],
    ['flavouring', 'Flavourings'],
    ['sweetener', 'Sweeteners'],
    ['salt', 'Salts'],
    ['nut-seed', 'Nuts & seeds'],
    ['other', 'Other'],
  ];
  const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((l) => [l, l]);
  const label = (list, v) => (list.find((x) => x[0] === v) || [v, v])[1];

  const FACETS = [['cat', KINDS], ['letter', LETTERS]];

  /* ---------- read every card once ---------- */
  const grid = $('ckg-grid');
  const CARDS = [...app.querySelectorAll('.ckg-ccard')].map((el) => {
    const d = el.dataset;
    let prods = [];
    try { prods = JSON.parse(d.prods || '[]'); } catch { prods = []; }
    return {
      el,
      h: d.h,
      url: d.url,
      title: d.title || '',
      cat: d.cat || 'other',
      catword: d.catword || '',
      letter: (d.letter || '').toUpperCase(),
      count: num(d.count) || 0,
      sub: d.sub || '',
      taste: d.taste || '',
      caf: d.caf || '',
      lede: d.lede || '',
      img: d.img || '',
      prods: Array.isArray(prods) ? prods : [],
      text: [d.title, d.sub, d.taste, d.lede, (Array.isArray(prods) ? prods : []).map((p) => p.t).join(' ')]
        .join(' ').toLowerCase(),
    };
  });

  /* Alphabetical, once, before anything renders. */
  CARDS.sort((a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }));
  CARDS.forEach((c) => grid.appendChild(c.el));

  /* The fifty check. */
  const expected = num(grid.dataset.expected);
  if (expected && CARDS.length < expected) {
    console.warn('[ingredients] %d cards rendered but the store has %d profiles — Liquid\'s per-type ceiling? See sections/ingredients-router.liquid.', CARDS.length, expected);
  }

  /* A page of the shelf, as the Showroom pages: the same pager under the grid, the same buttons.
     Client-side, so the chips narrow the WHOLE set and the alphabetical order holds across pages;
     a server page would be store order — the order the profiles were written in. */
  const PER = Math.max(12, num(grid.dataset.perPage) || 48);
  const S = { cat: new Set(), letter: new Set(), q: '', sel: null, page: 1 };

  const matches = (c, except) => {
    if (S.q && !S.q.split(/\s+/).every((w) => c.text.includes(w))) return false;
    for (const [key] of FACETS) {
      if (except !== key && S[key].size && !S[key].has(c[key])) return false;
    }
    return true;
  };

  function facetRow(key, list) {
    const pool = CARDS.filter((c) => matches(c, key));
    const counts = new Map();
    pool.forEach((c) => counts.set(c[key], (counts.get(c[key]) || 0) + 1));
    const vals = list.filter((x) => counts.get(x[0]));
    if (!vals.length || (vals.length === 1 && counts.get(vals[0][0]) === pool.length && !S[key].size)) return '';
    /* The letter row is a row of letters, not a row of pills with counts: twenty-odd counts in a
       line is noise, and the strip below already says how many. */
    const compact = key === 'letter';
    return `<div class="sr-facet${compact ? ' ckg-rfacets--letters' : ''}"><div class="sr-chiprow">${vals.map(
      ([v, l]) => `<button class="sr-chip" type="button" data-f="${key}" data-v="${esc(v)}" aria-pressed="${S[key].has(v)}">${
        esc(l)}${compact ? '' : `<span class="n">${counts.get(v)}</span>`}</button>`).join('')}</div></div>`;
  }

  function buildFacets() {
    $('ckg-facets').innerHTML = FACETS.map(([key, list]) => facetRow(key, list)).join('');
    $('ckg-facets').querySelectorAll('.sr-chip').forEach((b) => (b.onclick = () => {
      const set = S[b.dataset.f];
      /* A letter is a place in a list, not a set: picking B after A moves you, it does not add. */
      if (b.dataset.f === 'letter') {
        const was = set.has(b.dataset.v);
        set.clear();
        if (!was) set.add(b.dataset.v);
      } else {
        set.has(b.dataset.v) ? set.delete(b.dataset.v) : set.add(b.dataset.v);
      }
      track('shelf_filter', { facet: b.dataset.f, value: b.dataset.v, on: set.has(b.dataset.v) });
      /* A narrower set starts at its first page: page 3 of "Herbs" is not page 3 of everything. */
      S.page = 1;
      render();
    }));
  }

  /* The Showroom's pager: step buttons at either end that stay put (disabled) at the first and
     last page, the first and last numbers always, and the neighbours of the current one. */
  function renderFoot(pages) {
    const foot = $('ckg-foot');
    if (!foot) return;
    if (pages < 2) { foot.innerHTML = ''; return; }
    const want = new Set([1, pages, S.page, S.page - 1, S.page + 1]);
    const list = [...want].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
    const arrow = (d) => `<svg class="sr-pg__ico" viewBox="0 0 20 20" aria-hidden="true"><path d="${
      d < 0 ? 'm12 4-5 6 5 6' : 'm8 4 5 6-5 6'}"/></svg>`;
    const step = (n, d, word) => `<button class="sr-pg sr-pg--step" type="button" data-ckg-page="${n}"
      ${n < 1 || n > pages ? 'disabled' : ''} aria-label="${word} page">${
      d < 0 ? arrow(d) + `<span>${word}</span>` : `<span>${word}</span>` + arrow(d)}</button>`;
    let out = step(S.page - 1, -1, 'Previous') + '<span class="sr-pg__nums">';
    list.forEach((n, i) => {
      if (i && n - list[i - 1] > 1) out += '<span class="sr-pg__gap" aria-hidden="true">…</span>';
      out += `<button class="sr-pg" type="button" data-ckg-page="${n}"
        aria-current="${S.page === n ? 'page' : 'false'}"
        aria-label="Page ${n} of ${pages}">${n}</button>`;
    });
    out += '</span>' + step(S.page + 1, 1, 'Next');
    foot.innerHTML = out;
  }

  function render() {
    const hits = CARDS.filter((c) => matches(c));
    const pages = Math.max(1, Math.ceil(hits.length / PER));
    S.page = Math.min(Math.max(1, S.page), pages);
    const from = (S.page - 1) * PER;
    const shown = new Set(hits.slice(from, from + PER).map((c) => c.h));
    CARDS.forEach((c) => { c.el.hidden = !shown.has(c.h); });
    CARDS.forEach((c) => c.el.classList.toggle('is-sel', c.h === S.sel));

    buildFacets();
    renderFoot(pages);

    const active = FACETS.reduce((n, [key]) => n + S[key].size, 0) + (S.q ? 1 : 0);
    const clear = $('ckg-clear');
    clear.disabled = !active;
    clear.textContent = 'Clear all' + (active ? ` (${active})` : '');

    const bits = FACETS
      .filter(([key]) => S[key].size)
      .map(([key, list]) => [...S[key]].map((v) => (key === 'letter' ? `starting with ${v}` : label(list, v).toLowerCase())).join(' or '));
    const strip = $('ckg-strip');
    const range = hits.length > PER ? ` · ${from + 1}–${Math.min(hits.length, from + PER)}` : '';
    strip.innerHTML = `<span><b>${hits.length}</b> ${hits.length === 1 ? 'ingredient' : 'ingredients'}${range}${
      S.q ? ` matching “${esc(S.q)}”` : ''}${bits.length ? ' · ' + esc(bits.join(' · ')) : ''}</span>${
      active ? '<button class="sr-cta" data-ckg-widen type="button">Show everything</button>' : ''}`;

    let empty = $('ckg-empty');
    if (!hits.length) {
      if (!empty) {
        empty = document.createElement('div');
        empty.id = 'ckg-empty';
        empty.className = 'sr-empty';
        grid.appendChild(empty);
      }
      empty.innerHTML = `<p>No ingredient matches ${S.q ? `“${esc(S.q)}”` : 'those'}.</p>
        <div class="sr-empty__acts"><button class="sr-cta" data-ckg-widen type="button">Show everything</button>
        ${S.q ? `<a class="sr-cta" href="/search?q=${encodeURIComponent(S.q)}">Search the whole shop</a>` : ''}</div>`;
      empty.hidden = false;
    } else if (empty) empty.hidden = true;

    renderRail();
    writeUrl();
  }

  /* ---------- the rail ---------- */
  function renderRail() {
    const rail = $('ckg-rail');
    if (!rail) return;
    const c = CARDS.find((x) => x.h === S.sel);
    if (!c) {
      rail.innerHTML = '<div class="sr-rail__empty">Pick any ingredient and it opens here — what it is, what it tastes like, and everything on the shelf that carries it — without losing your place in the grid.</div>';
      return;
    }
    rail.innerHTML = railHTML(c);
  }

  /* The detail fragment, shared by the rail and the phone's sheet so the two cannot drift. */
  function railHTML(c) {
    /* Never state an absence as a finding: no products, no list. The list is the whole list — every
       product the profile links, carried on the card (Dayton, 2026-09-21). */
    const list = c.prods.length
      ? `<h4>On the shelf with it <span class="ckg-rail__n">${c.count}</span></h4>
         <ul class="ckg-rail__list">${c.prods.map((p) => `<li${p.a ? '' : ' class="is-out"'}>
           <a href="/products/${encodeURIComponent(p.h)}"><span>${esc(p.t)}</span>
           <b>${p.a ? esc(p.p) : 'Sold out'}</b></a></li>`).join('')}</ul>
`
      : '';

    return `<div class="ckg-rail">
      ${c.img ? `<img class="sr-rail__img" src="${esc(c.img)}" alt="" loading="lazy">` : ''}
      <div class="sr-rail__b">
        <h3 class="sr-rail__t">${esc(c.title)}</h3>
        ${c.sub ? `<p class="sr-rail__sub"><i>${esc(c.sub)}</i></p>` : ''}
        ${c.taste ? `<p class="ckg-rail__hook">${esc(c.taste)}</p>` : ''}
        ${c.lede ? `<p>${esc(c.lede)}</p>` : ''}
        ${c.caf ? `<p class="ckg-rail__caf">${esc(c.caf)}</p>` : ''}
        <div class="ckg-rail__go"><a class="sr-btn" href="${esc(c.url)}">Read about it</a></div>
        <div class="ckg-rail__listwrap">${list}</div>
      </div>
    </div>`;
  }

  /* ---------- the sheet: the rail's job on a phone ---------- */
  let cardSheet = null;
  function openCardSheet(handle) {
    const c = CARDS.find((x) => x.h === handle);
    if (!c) return;
    if (!window.SRSheet) { window.location.href = c.url; return; }
    if (!cardSheet) {
      cardSheet = window.SRSheet.create({
        ariaLabel: 'Ingredient',
        onClose: () => { S.sel = null; CARDS.forEach((x) => x.el.classList.remove('is-sel')); },
      });
    }
    if (S.sel !== handle) track('rail_open', { handle });
    S.sel = handle;
    CARDS.forEach((x) => x.el.classList.toggle('is-sel', x.h === handle));
    cardSheet.setContent('<div class="sr-sheet__scroll"><div class="sr-rail">' + railHTML(c) + '</div></div>');
    cardSheet.open();
  }

  /* ---------- url state ---------- */
  const PARAM = { cat: 'k', letter: 'l' };
  function writeUrl() {
    const u = new URL(window.location);
    FACETS.forEach(([key]) => (S[key].size
      ? u.searchParams.set(PARAM[key], [...S[key]].join(','))
      : u.searchParams.delete(PARAM[key])));
    /* `pg`, not `page` — Shopify reserves `page` on storefront routes. */
    if (S.page > 1) u.searchParams.set('pg', String(S.page)); else u.searchParams.delete('pg');
    history.replaceState(null, '', u);
  }
  function readUrl() {
    const u = new URLSearchParams(window.location.search);
    FACETS.forEach(([key]) => (u.get(PARAM[key]) || '').split(',').filter(Boolean).forEach((v) => S[key].add(v)));
    S.page = Math.max(1, parseInt(u.get('pg') || '1', 10) || 1);
  }

  /* ---------- wiring ---------- */
  const railVisible = () => !!($('ckg-rail') && $('ckg-rail').offsetParent);

  function select(handle) {
    if (S.sel !== handle) track('rail_open', { handle });
    S.sel = S.sel === handle ? null : handle;
    CARDS.forEach((c) => c.el.classList.toggle('is-sel', c.h === S.sel));
    renderRail();
  }

  app.addEventListener('click', (e) => {
    const door = e.target.closest('.ckg-ccard__door');
    if (door && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      const card = door.closest('.ckg-ccard');
      if (card) (railVisible() ? select : openCardSheet)(card.dataset.h);
      return;
    }
    const open = e.target.closest('[data-ckg-open]');
    if (open) {
      e.preventDefault();
      (railVisible() ? select : openCardSheet)(open.dataset.ckgOpen);
      return;
    }
    if (e.target.closest('[data-ckg-widen]')) {
      reset();
      render();
      return;
    }
    const pg = e.target.closest('[data-ckg-page]');
    if (pg && !pg.disabled) {
      S.page = Number(pg.dataset.ckgPage) || 1;
      render();
      /* Back to the top of the shelf, so a new page starts where a page starts. */
      window.scrollTo({ top: app.getBoundingClientRect().top + window.scrollY - 12, behavior: 'smooth' });
    }
  });

  function reset() {
    FACETS.forEach(([key]) => S[key].clear());
    S.q = '';
    S.page = 1;
  }
  $('ckg-clear').onclick = () => { reset(); render(); };

  readUrl();
  render();
})();
